// 榜单奖牌(图片 + 代码合成数字)+ 段位徽章(图片 + 数字胶囊)。
// 奖牌资源见 docs/geodle-leaderboard-medal-prompts.md,由 scripts/gen-medals.py 生成。
import type { Board } from '../lib/api';
import { useI18n } from '../i18n';

/**
 * 四套奖牌的「字盘占整枚比例」不同(Today 素圈 62–65%,其余带芒/叶片只有 42–52%),
 * 同框显示时 Today 会显得胖一圈。用两个方向相反的系数把**可视主体**拉齐:
 *   KSIZE 调盒子 —— Today 缩小、Unlimited 放大
 *   KFONT 调字号 —— Today 盒子小但字盘大,数字反而要放大才与其余齐平
 */
const KSIZE: Record<string, number> = { today: 0.84, streak: 1, unlimited: 1.12, level: 1 };
const KFONT: Record<string, number> = { today: 1.22, streak: 1, unlimited: 1, level: 1 };

/** 数字位数分档 —— 持有徽章的天数可能到三位 */
function fontScale(text: string): number {
  if (text.length >= 4) return 0.185;
  if (text.length === 3) return 0.23;
  return 0.3;
}

export function Medal({
  board = 'today',
  rank,
  num,
  size = 26,
}: {
  board?: Board;
  /** 名次:决定用哪一枚(金/银/铜) */
  rank: number;
  /** 盘面数字。省略时显示名次;持有徽章传入霸榜天数 */
  num?: number;
  size?: number;
}) {
  const { t } = useI18n();
  const r = Math.max(1, Math.min(3, Math.floor(rank)));
  const px = Math.round(size * (KSIZE[board] ?? 1));
  // 盘面只刻「牌面本身说不出的信息」(霸榜天数)。名次由金/银/铜自明,不再压一个数字上去。
  const text = num == null ? null : num >= 1000 ? '999+' : String(num);
  const fs = text ? Math.max(7, Math.round(px * fontScale(text) * (KFONT[board] ?? 1))) : 0;
  return (
    <span className="medal" role="img" style={{ width: px, height: px }} aria-label={`${t.lb[board]} · #${r}${text && num !== r ? ` · ${text}` : ''}`}>
      <img src={`/medals/medal-${board}-${r}.webp`} alt="" width={px} height={px} loading="lazy" decoding="async" />
      {text && <b style={{ fontSize: fs }}>{text}</b>}
    </span>
  );
}

// 段位徽章:「旅程载具」主题的圆形铜版雕刻徽章 + 代码合成的等级数字胶囊。
// 徽章图见 docs/geodle-rank-badge-prompts.md;资源由 scripts/gen-badges.py 生成。
// 数字不画进图里 —— 由代码叠加,保证 14 级字形完全一致。
export const MAX_RANK = 14;

export function RankBadge({ level, size = 34 }: { level: number; size?: number }) {
  const { t } = useI18n();
  const lv = Math.max(1, Math.min(MAX_RANK, Math.floor(level) || 1));
  const src = `/badges/lv${lv}-${size > 96 ? 256 : 80}.webp`;
  return (
    <span className="rank-badge-img" role="img" style={{ width: size, height: size }} aria-label={`${t.lb.level} ${lv}`}>
      <img src={src} alt="" width={size} height={size} loading="lazy" decoding="async" />
      <b className="rank-pip" style={{ fontSize: Math.max(8, Math.round(size * 0.23)) }}>
        {lv}
      </b>
    </span>
  );
}
